import React, { useState, useEffect, useMemo } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Sparkles, Heart, Shield, Zap, Moon, Sun, Eye, Send, CheckCircle, ChevronRight, Star } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ZODIAC_SIGNS = [
  { name: 'Aries', symbol: '\u2648', element: 'Fire', dates: 'Mar 21 - Apr 19', color: '#E74C3C',
    baseCrystals: ['Carnelian', 'Red Jasper', 'Bloodstone'], traits: 'Bold, Ambitious, Energetic' },
  { name: 'Taurus', symbol: '\u2649', element: 'Earth', dates: 'Apr 20 - May 20', color: '#27AE60',
    baseCrystals: ['Rose Quartz', 'Emerald', 'Rhodonite'], traits: 'Reliable, Patient, Devoted' },
  { name: 'Gemini', symbol: '\u264A', element: 'Air', dates: 'May 21 - Jun 20', color: '#F39C12',
    baseCrystals: ['Citrine', 'Tiger\'s Eye', 'Agate'], traits: 'Adaptable, Curious, Expressive' },
  { name: 'Cancer', symbol: '\u264B', element: 'Water', dates: 'Jun 21 - Jul 22', color: '#C0C0C0',
    baseCrystals: ['Moonstone', 'Pearl', 'Selenite'], traits: 'Intuitive, Nurturing, Protective' },
  { name: 'Leo', symbol: '\u264C', element: 'Fire', dates: 'Jul 23 - Aug 22', color: '#FF9F43',
    baseCrystals: ['Tiger\'s Eye', 'Sunstone', 'Pyrite'], traits: 'Confident, Generous, Creative' },
  { name: 'Virgo', symbol: '\u264D', element: 'Earth', dates: 'Aug 23 - Sep 22', color: '#00B4D8',
    baseCrystals: ['Amazonite', 'Green Jade', 'Moss Agate'], traits: 'Analytical, Kind, Hardworking' },
  { name: 'Libra', symbol: '\u264E', element: 'Air', dates: 'Sep 23 - Oct 22', color: '#3B82F6',
    baseCrystals: ['Lapis Lazuli', 'Lepidolite', 'Opal'], traits: 'Harmonious, Fair, Social' },
  { name: 'Scorpio', symbol: '\u264F', element: 'Water', dates: 'Oct 23 - Nov 21', color: '#4A4A6A',
    baseCrystals: ['Black Obsidian', 'Malachite', 'Labradorite'], traits: 'Passionate, Resourceful, Brave' },
  { name: 'Sagittarius', symbol: '\u2650', element: 'Fire', dates: 'Nov 22 - Dec 21', color: '#9B59B6',
    baseCrystals: ['Turquoise', 'Lapis Lazuli', 'Sodalite'], traits: 'Optimistic, Adventurous, Honest' },
  { name: 'Capricorn', symbol: '\u2651', element: 'Earth', dates: 'Dec 22 - Jan 19', color: '#5D4E37',
    baseCrystals: ['Garnet', 'Onyx', 'Smoky Quartz'], traits: 'Disciplined, Responsible, Patient' },
  { name: 'Aquarius', symbol: '\u2652', element: 'Air', dates: 'Jan 20 - Feb 18', color: '#6C5CE7',
    baseCrystals: ['Amethyst', 'Aquamarine', 'Fluorite'], traits: 'Independent, Humanitarian, Original' },
  { name: 'Pisces', symbol: '\u2653', element: 'Water', dates: 'Feb 19 - Mar 20', color: '#7FDBFF',
    baseCrystals: ['Aquamarine', 'Amethyst', 'Blue Lace Agate'], traits: 'Compassionate, Intuitive, Artistic' },
];

const SYMPTOMS = [
  { id: 'anxiety', label: 'Anxiety & Stress', icon: Shield, color: '#6C5CE7',
    crystals: ['Amethyst', 'Lepidolite', 'Blue Lace Agate', 'Howlite'],
    protocol: 'Place Lepidolite on your third eye during 10-minute breathwork. Keep Amethyst on your nightstand.' },
  { id: 'insomnia', label: 'Insomnia & Restlessness', icon: Moon, color: '#3498DB',
    crystals: ['Selenite', 'Moonstone', 'Howlite', 'Amethyst'],
    protocol: 'Create a selenite grid around your pillow. Hold Moonstone while repeating your sleep intention.' },
  { id: 'low_energy', label: 'Low Energy & Fatigue', icon: Zap, color: '#F39C12',
    crystals: ['Carnelian', 'Citrine', 'Red Jasper', 'Sunstone'],
    protocol: 'Wear Carnelian as a pendant over your sacral chakra. Citrine in your left pocket for solar activation.' },
  { id: 'heartbreak', label: 'Heartbreak & Grief', icon: Heart, color: '#E74C3C',
    crystals: ['Rose Quartz', 'Rhodonite', 'Green Aventurine', 'Morganite'],
    protocol: 'Sleep with Rose Quartz over your heart. Bath ritual: Add Rhodonite to warm water with pink Himalayan salt.' },
  { id: 'confusion', label: 'Mental Fog & Indecision', icon: Eye, color: '#9B59B6',
    crystals: ['Clear Quartz', 'Fluorite', 'Sodalite', 'Lapis Lazuli'],
    protocol: 'Meditate with Fluorite on your third eye for 7 minutes. Keep Clear Quartz on your desk while working.' },
  { id: 'financial', label: 'Financial Blocks', icon: Star, color: '#27AE60',
    crystals: ['Citrine', 'Green Aventurine', 'Pyrite', 'Tiger\'s Eye'],
    protocol: 'Place Citrine in the wealth corner (far left from entrance). Carry Pyrite in your wallet.' },
  { id: 'protection', label: 'Negative Energy & EMF', icon: Shield, color: '#2C3E50',
    crystals: ['Black Tourmaline', 'Obsidian', 'Shungite', 'Smoky Quartz'],
    protocol: 'Black Tourmaline at each corner of your home. Shungite near electronics. Obsidian at the front door.' },
  { id: 'creativity', label: 'Creative Block', icon: Sun, color: '#FF6B9D',
    crystals: ['Carnelian', 'Orange Calcite', 'Moonstone', 'Labradorite'],
    protocol: 'Hold Carnelian during brainstorming. Place Labradorite where you create. Moonstone under the full moon.' },
];

const MOON_PHASES = [
  { name: 'New Moon', amplifier: 'Selenite', note: 'New Moon amplifies intention-setting. Selenite clears old patterns.' },
  { name: 'Waxing Crescent', amplifier: 'Citrine', note: 'Growth phase. Citrine accelerates manifestation momentum.' },
  { name: 'First Quarter', amplifier: 'Carnelian', note: 'Action phase. Carnelian provides the fire to push forward.' },
  { name: 'Waxing Gibbous', amplifier: 'Rose Quartz', note: 'Refinement phase. Rose Quartz softens any resistance.' },
  { name: 'Full Moon', amplifier: 'Clear Quartz', note: 'Peak energy. Clear Quartz is the master amplifier for all intentions.' },
  { name: 'Waning Gibbous', amplifier: 'Amethyst', note: 'Gratitude phase. Amethyst deepens spiritual awareness.' },
  { name: 'Last Quarter', amplifier: 'Black Tourmaline', note: 'Release phase. Black Tourmaline absorbs what no longer serves you.' },
  { name: 'Waning Crescent', amplifier: 'Moonstone', note: 'Rest phase. Moonstone nurtures your inner world before renewal.' },
];

function getMoonPhase() {
  const d = new Date();
  const year = d.getFullYear();
  let month = d.getMonth() + 1;
  const day = d.getDate();
  let c, e;
  if (month < 3) { c = year - 1; e = month + 12; } else { c = year; e = month; }
  let jd = Math.floor(365.25 * c) + Math.floor(30.6001 * (e + 1)) + day - 694039.09;
  jd /= 29.5305882;
  jd -= Math.floor(jd);
  const idx = Math.round(jd * 8) % 8;
  return MOON_PHASES[idx];
}

const HealingPrescriptionPage = () => {
  usePageTitle('Crystal Healing Prescription - Personalized Crystal Kit');

  const [step, setStep] = useState(1);
  const [selectedSign, setSelectedSign] = useState(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [prescription, setPrescription] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', address: '' });

  const moonPhase = useMemo(() => getMoonPhase(), []);

  const toggleSymptom = (id) => {
    setSelectedSymptoms(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : prev.length < 3 ? [...prev, id] : prev
    );
  };

  const generatePrescription = () => {
    if (!selectedSign || selectedSymptoms.length === 0) return;

    const symptomData = selectedSymptoms.map(id => SYMPTOMS.find(s => s.id === id));
    const allCrystals = new Map();

    // Score crystals based on zodiac base
    selectedSign.baseCrystals.forEach(c => {
      allCrystals.set(c, (allCrystals.get(c) || 0) + 3);
    });
    // Score crystals from symptoms
    symptomData.forEach(s => {
      s.crystals.forEach((c, i) => {
        allCrystals.set(c, (allCrystals.get(c) || 0) + (4 - i));
      });
    });
    // Moon phase amplifier bonus
    allCrystals.set(moonPhase.amplifier, (allCrystals.get(moonPhase.amplifier) || 0) + 5);

    // Top 5 crystals
    const sorted = [...allCrystals.entries()].sort((a, b) => b[1] - a[1]);
    const topCrystals = sorted.slice(0, 5).map(([name]) => name);

    // Build protocols
    const protocols = symptomData.map(s => ({
      symptom: s.label,
      protocol: s.protocol,
    }));

    setPrescription({
      zodiac: selectedSign,
      moonPhase,
      symptoms: symptomData,
      crystals: topCrystals,
      protocols,
      lunarNote: moonPhase.note,
    });
    setStep(3);
  };

  const handleFormChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePurchase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/prescription/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          zodiacSign: selectedSign.name,
          symptoms: selectedSymptoms,
          crystals: prescription.crystals,
          moonPhase: moonPhase.name,
          amount: 125,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        toast.success('Prescription order received!');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      {/* Hero */}
      <div className="relative py-20 px-6 bg-gradient-to-b from-white to-[#faf8f5] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-1/4 w-80 h-80 bg-[#9D4EDD]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/3 w-96 h-96 bg-[#FF6B9D]/10 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#9D4EDD]/30 bg-[#9D4EDD]/5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#9D4EDD]" />
            <span className="text-xs text-[#9D4EDD] tracking-[3px] uppercase font-bold">Personalized Crystal Healing</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1e1919] mb-4" data-testid="prescription-title">
            Healing <span className="text-[#9D4EDD]">Prescription</span>
          </h1>
          <p className="text-[#61525a] max-w-xl mx-auto text-base leading-relaxed mb-6">
            Your zodiac alignment, the current lunar phase, and your specific symptoms
            are cross-referenced to compose a crystal kit that is calibrated exclusively for you.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-[#7A7A9A]">
            <span>Zodiac + Moon + Symptoms</span>
            <span className="w-1 h-1 rounded-full bg-[#c9a96e]" />
            <span className="text-[#c9a96e] font-semibold">$125 per prescription</span>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-center gap-4 mb-12">
          {[
            { num: 1, label: 'Zodiac Sign' },
            { num: 2, label: 'Symptoms' },
            { num: 3, label: 'Prescription' },
          ].map((s, i) => (
            <React.Fragment key={s.num}>
              {i > 0 && <div className={`w-12 h-0.5 ${step >= s.num ? 'bg-[#c9a96e]' : 'bg-[#e8e4de]'}`} />}
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s.num ? 'bg-[#c9a96e] text-white' : 'bg-[#e8e4de] text-[#7A7A9A]'
                }`}>{s.num}</div>
                <span className={`text-sm hidden sm:block ${step >= s.num ? 'text-[#1e1919] font-semibold' : 'text-[#7A7A9A]'}`}>{s.label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Zodiac Selection */}
        {step === 1 && (
          <div data-testid="step-zodiac">
            <h2 className="font-serif text-2xl text-[#1e1919] text-center mb-2">Select Your Zodiac Sign</h2>
            <p className="text-center text-[#7A7A9A] text-sm mb-8">Your birth sign determines your base crystal affinity</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {ZODIAC_SIGNS.map(sign => (
                <button
                  key={sign.name}
                  onClick={() => setSelectedSign(sign)}
                  className={`p-4 rounded-2xl text-center transition-all border-2 ${
                    selectedSign?.name === sign.name
                      ? 'border-[#c9a96e] bg-[#c9a96e]/10 shadow-lg scale-105'
                      : 'border-transparent bg-white hover:border-[#e8e4de] hover:shadow-md'
                  }`}
                  data-testid={`zodiac-select-${sign.name.toLowerCase()}`}
                >
                  <span className="text-3xl block mb-1" style={{ color: sign.color }}>{sign.symbol}</span>
                  <span className="text-xs font-bold text-[#1e1919]">{sign.name}</span>
                  <span className="text-[10px] text-[#7A7A9A] block">{sign.dates}</span>
                </button>
              ))}
            </div>
            {selectedSign && (
              <div className="mt-8 text-center">
                <div className="inline-block bg-white rounded-xl p-4 shadow-sm border border-[#e8e4de] mb-4">
                  <span className="text-2xl mr-2">{selectedSign.symbol}</span>
                  <span className="font-serif text-lg text-[#1e1919]">{selectedSign.name}</span>
                  <span className="text-sm text-[#7A7A9A] ml-2">{selectedSign.element} Sign</span>
                  <p className="text-xs text-[#9D4EDD] mt-1">Base crystals: {selectedSign.baseCrystals.join(', ')}</p>
                </div>
                <div>
                  <button
                    onClick={() => setStep(2)}
                    className="px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-lg transition-all flex items-center gap-2 mx-auto"
                    data-testid="step1-next"
                  >
                    Continue <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Symptoms */}
        {step === 2 && (
          <div data-testid="step-symptoms">
            <h2 className="font-serif text-2xl text-[#1e1919] text-center mb-2">What Needs Healing?</h2>
            <p className="text-center text-[#7A7A9A] text-sm mb-8">Select up to 3 symptoms for your personalized prescription</p>

            <div className="grid sm:grid-cols-2 gap-3">
              {SYMPTOMS.map(symptom => {
                const Icon = symptom.icon;
                const selected = selectedSymptoms.includes(symptom.id);
                return (
                  <button
                    key={symptom.id}
                    onClick={() => toggleSymptom(symptom.id)}
                    className={`p-4 rounded-xl text-left transition-all border-2 flex items-center gap-4 ${
                      selected
                        ? 'border-[#c9a96e] bg-[#c9a96e]/5 shadow-md'
                        : 'border-transparent bg-white hover:border-[#e8e4de] hover:shadow-sm'
                    }`}
                    data-testid={`symptom-${symptom.id}`}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${symptom.color}15` }}>
                      <Icon className="w-5 h-5" style={{ color: symptom.color }} />
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-[#1e1919]">{symptom.label}</p>
                      <p className="text-[10px] text-[#7A7A9A]">{symptom.crystals.slice(0, 2).join(', ')} + more</p>
                    </div>
                    {selected && (
                      <div className="ml-auto w-6 h-6 rounded-full bg-[#c9a96e] flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-xl">
              <p className="text-xs text-[#7A7A9A] mb-1">Current Lunar Phase</p>
              <p className="font-serif text-lg text-[#1e1919]">{moonPhase.name}</p>
              <p className="text-sm text-[#9D4EDD]">Amplifier: {moonPhase.amplifier}</p>
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button onClick={() => setStep(1)} className="text-sm text-[#7A7A9A] hover:text-[#1e1919]">Back</button>
              <button
                onClick={generatePrescription}
                disabled={selectedSymptoms.length === 0}
                className="px-8 py-3 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-lg transition-all disabled:opacity-40 flex items-center gap-2"
                data-testid="generate-prescription"
              >
                Generate Prescription <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Prescription Result */}
        {step === 3 && prescription && (
          <div data-testid="step-prescription">
            <div className="bg-white rounded-3xl shadow-xl border border-[#e8e4de] overflow-hidden">
              {/* Prescription Header */}
              <div className="bg-gradient-to-r from-[#f8f5ff] to-[#fff5f8] p-8 text-center border-b border-[#e8e4de]">
                <p className="text-xs text-[#9D4EDD] uppercase tracking-[3px] mb-2">Your Personal Healing Prescription</p>
                <h2 className="font-serif text-3xl text-[#1e1919] mb-2">
                  {prescription.zodiac.symbol} {prescription.zodiac.name} &middot; {prescription.moonPhase.name}
                </h2>
                <p className="text-[#7A7A9A] text-sm">{prescription.zodiac.traits}</p>
              </div>

              <div className="p-8">
                {/* Prescribed Crystals */}
                <div className="mb-8">
                  <h3 className="font-serif text-xl text-[#1e1919] mb-4">Prescribed Crystal Kit</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {prescription.crystals.map((crystal, i) => (
                      <div key={crystal} className="bg-[#f8f5ff] rounded-xl p-4 text-center border border-[#9D4EDD]/10">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] mx-auto mb-2 flex items-center justify-center text-white font-bold text-sm">
                          {i + 1}
                        </div>
                        <p className="font-semibold text-sm text-[#1e1919]">{crystal}</p>
                        {i === 0 && <span className="text-[9px] text-[#c9a96e] uppercase tracking-wider">Primary</span>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Healing Protocols */}
                <div className="mb-8">
                  <h3 className="font-serif text-xl text-[#1e1919] mb-4">Healing Protocols</h3>
                  <div className="space-y-3">
                    {prescription.protocols.map((p, i) => (
                      <div key={i} className="bg-[#faf8f5] rounded-xl p-4 border border-[#e8e4de]">
                        <p className="text-xs text-[#9D4EDD] font-bold uppercase mb-1">{p.symptom}</p>
                        <p className="text-sm text-[#4A4A6A] leading-relaxed">{p.protocol}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lunar Note */}
                <div className="bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-xl p-4 mb-8 border border-[#9D4EDD]/20">
                  <p className="text-xs text-[#9D4EDD] font-bold uppercase mb-1">Lunar Amplification</p>
                  <p className="text-sm text-[#4A4A6A]">{prescription.lunarNote}</p>
                  <p className="text-xs text-[#c9a96e] mt-2">Moon Phase Amplifier Crystal: <strong>{prescription.moonPhase.amplifier}</strong></p>
                </div>

                {/* Purchase Section */}
                {!submitted ? (
                  <div className="border-t border-[#e8e4de] pt-8">
                    <div className="text-center mb-6">
                      <p className="font-serif text-2xl text-[#1e1919] mb-1">Receive Your Crystal Kit</p>
                      <p className="text-[#7A7A9A] text-sm">Hand-selected, energetically charged, and shipped to your door</p>
                      <p className="font-serif text-4xl text-[#c9a96e] mt-4">$125</p>
                      <p className="text-xs text-[#7A7A9A]">5 crystals + healing protocol card + velvet pouch</p>
                    </div>

                    {!showPayment ? (
                      <button
                        onClick={() => setShowPayment(true)}
                        className="w-full py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold text-lg hover:shadow-2xl hover:shadow-[#c9a96e]/30 transition-all"
                        data-testid="order-prescription-btn"
                      >
                        Order My Prescription Kit — $125
                      </button>
                    ) : (
                      <form onSubmit={handlePurchase} className="space-y-3" data-testid="prescription-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <input type="text" name="fullName" placeholder="Full Name *" value={formData.fullName} onChange={handleFormChange} required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#c9a96e] focus:border-transparent outline-none text-sm" data-testid="rx-fullname" />
                          <input type="email" name="email" placeholder="Email *" value={formData.email} onChange={handleFormChange} required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#c9a96e] focus:border-transparent outline-none text-sm" data-testid="rx-email" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <input type="tel" name="phone" placeholder="Phone *" value={formData.phone} onChange={handleFormChange} required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#c9a96e] focus:border-transparent outline-none text-sm" data-testid="rx-phone" />
                          <input type="text" name="address" placeholder="Shipping Address *" value={formData.address} onChange={handleFormChange} required
                            className="w-full px-4 py-3 rounded-xl border border-[#e8e4de] focus:ring-2 focus:ring-[#c9a96e] focus:border-transparent outline-none text-sm" data-testid="rx-address" />
                        </div>
                        <p className="text-[10px] text-[#7A7A9A] text-center">Payment processing via Zoho Payments (coming soon). Your order is recorded and you will be contacted to complete payment.</p>
                        <button type="submit" disabled={submitting}
                          className="w-full py-4 bg-gradient-to-r from-[#c9a96e] to-[#FFB800] text-[#1e1919] rounded-xl font-bold hover:shadow-2xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                          data-testid="rx-submit"
                        >
                          {submitting ? <div className="w-5 h-5 border-2 border-[#1e1919]/30 border-t-[#1e1919] rounded-full animate-spin" /> : <><Send className="w-4 h-4" /> Complete Order — $125</>}
                        </button>
                      </form>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="prescription-success">
                    <div className="w-16 h-16 rounded-full bg-[#00C853]/10 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-[#00C853]" />
                    </div>
                    <h3 className="font-serif text-2xl text-[#1e1919] mb-2">Prescription Order Confirmed</h3>
                    <p className="text-[#7A7A9A] max-w-md mx-auto text-sm">
                      Your personalized crystal kit is being prepared. You'll receive an email
                      with tracking information within 2 business days.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Start Over */}
            <div className="text-center mt-8">
              <button onClick={() => { setStep(1); setSelectedSign(null); setSelectedSymptoms([]); setPrescription(null); setShowPayment(false); setSubmitted(false); }}
                className="text-sm text-[#9D4EDD] hover:text-[#FF6B9D] transition-colors" data-testid="start-over">
                Generate Another Prescription
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Trademark */}
      <div className="py-8 text-center">
        <p className="text-[10px] text-[#7A7A9A]">Crystal Healing Prescriptions by Gemstone Minerals&reg; &middot; Kholey.com&reg;</p>
      </div>
    </div>
  );
};

export default HealingPrescriptionPage;
